import type { ReactElement } from 'react';
import { EquipmentCard } from './EquipmentCard.js';
import { Skeleton } from '../feedback/index.js';
import type { CatalogModel, TaxonomyNode } from '../../lib/api/queries/catalog.js';
import type { EquipmentSearchHit } from '../../lib/api/queries/equipment.js';

export interface EquipmentGridProps {
  units: EquipmentSearchHit[];
  /** Resolved from P-Q005 so a card can name the model behind `model_id`. */
  modelsById: Map<string, CatalogModel>;
  /**
   * P-Q004 by id, so a card can print the catalogue's own name for what the
   * machine IS. Optional: without it the cards simply carry no type line.
   */
  taxonomyById?: Map<string, TaxonomyNode>;
  /** Set when the search carried a period — see EquipmentCard. */
  matchedPeriod?: boolean;
}

/** One grid: 1 column below 640, 2 from 640, 3 from 1280. */
export function EquipmentGrid({
  units,
  modelsById,
  taxonomyById,
  matchedPeriod,
}: EquipmentGridProps): ReactElement {
  return (
    <ul className="eq-list stagger" aria-label="Results">
      {units.map((unit) => {
        const model = modelsById.get(unit.model_id);
        const category =
          model === undefined ? undefined : taxonomyById?.get(model.taxonomy_node_id)?.name;
        return (
          <EquipmentCard
            key={unit.id}
            unit={unit}
            model={model}
            {...(category === undefined ? {} : { category })}
            {...(matchedPeriod === undefined ? {} : { matchedPeriod })}
          />
        );
      })}
    </ul>
  );
}

/**
 * The loading shape of the grid above — same columns, same plate, same line
 * count, so content does not jump when it arrives (§5: a skeleton matches the
 * final layout's shape).
 */
export function EquipmentGridSkeleton({ count = 6 }: { count?: number }): ReactElement {
  return (
    <ul className="eq-list" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <li key={index} className="eq-card eq-card--loading">
          <span className="eq-card__plate" />
          <div className="eq-card__body">
            <Skeleton height={12} width="34%" />
            <Skeleton height={22} width="76%" />
            <Skeleton height={14} width="52%" />
            <Skeleton height={14} width="40%" />
          </div>
        </li>
      ))}
    </ul>
  );
}
